// CreateTaskScreen — director sets a task on a deal (M-3 §3.9, brought to full
// design parity per screens-createtask.jsx). Two modes:
//   • Вручную — deal + task type + responsible (round-robin or a rich manager
//     card with recommendation/overload/load-bar) + deadline + priority +
//     completion conditions + comment + notify-Telegram toggle.
//   • AI — dictate/describe the task in free text (Web Speech voice input) →
//     POST /director/parse-task → fills the manual form.
// The submit CTA drives the native Telegram MainButton with an in-screen
// fallback button for old clients / dev.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { createTask, fetchTeam, parseTask } from '../../api/director'
import { fetchDeals } from '../../api/deals'
import { fetchSearch } from '../../api/search'
import type { TaskPriority, TaskType, TeamMemberCard } from '../../api/types'
import { hapticNotify, isTelegramEnv } from '../../telegram/tg'
import { useMainButton } from '../../telegram/useMainButton'
import { Avatar, Badge, Button, Icon, Money, t, useToast } from '../../design'

type DueId = 'in1h' | 'today18' | 'tom10' | 'custom'
type Mode = 'manual' | 'ai'

const COND_IDS = ['call', 'stage', 'offer', 'amount'] as const
type CondId = (typeof COND_IDS)[number]

export function CreateTaskScreen() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [params] = useSearchParams()

  const presetDeal = params.get('deal_id')
  const presetManager = params.get('manager_id')

  // Dev/deeplink: ?ct_mode=ai opens straight on the AI tab (preview + demo).
  const [mode, setMode] = useState<Mode>(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ct_mode') === 'ai' ? 'ai' : 'manual',
  )
  const [dealId, setDealId] = useState<number | null>(presetDeal ? Number(presetDeal) : null)
  const [dealLabel, setDealLabel] = useState<string>(presetDeal ? `#${presetDeal}` : '')
  const [taskType, setTaskType] = useState<TaskType>('call')
  const [roundRobin, setRoundRobin] = useState(!presetManager)
  const [managerId, setManagerId] = useState<number | null>(presetManager ? Number(presetManager) : null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueSel, setDueSel] = useState<DueId>('today18')
  const [dueCustom, setDueCustom] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('normal')
  const [conds, setConds] = useState<Record<CondId, boolean>>({ call: true, stage: true, offer: false, amount: false })
  const [customConds, setCustomConds] = useState<string[]>([])
  const [customDraft, setCustomDraft] = useState('')
  const [notifyTg, setNotifyTg] = useState(true)
  const [dealOpen, setDealOpen] = useState(false)
  const [touched, setTouched] = useState(false)
  const [aiFilled, setAiFilled] = useState(false)
  // Refs on each required section so a failed submit can scroll to + glow the
  // first empty one (visual/DOM order: deal → responsible → title → deadline).
  const dealRef = useRef<HTMLDivElement>(null)
  const respRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const dueRef = useRef<HTMLDivElement>(null)

  // AI mode
  const [aiText, setAiText] = useState('')
  const [listening, setListening] = useState(false)
  const recRef = useRef<{ stop: () => void } | null>(null)
  const baseRef = useRef('')

  const team = useQuery({ queryKey: ['team'], queryFn: fetchTeam })
  const managers = team.data?.managers ?? []
  // Recommendation = least-loaded manager (design's AI pick).
  const recommendedId = useMemo(() => {
    if (managers.length === 0) return null
    return managers.reduce((best, m) => (m.stats.load_pct < best.stats.load_pct ? m : best), managers[0]).id
  }, [managers])

  const TYPES: { id: TaskType; icon: ReactNode; label: string }[] = [
    { id: 'call', icon: <Icon.Phone size={15} sw={2.25} />, label: t('ct.tt.call') },
    { id: 'message', icon: <Icon.MessageCircle size={15} sw={2.25} />, label: t('ct.tt.message') },
    { id: 'meeting', icon: <Icon.Calendar size={15} sw={2.25} />, label: t('ct.tt.meeting') },
    { id: 'doc', icon: <Icon.Mail size={15} sw={2.25} />, label: t('ct.tt.doc') },
  ]
  const COND_DEFS: { id: CondId; icon: ReactNode; label: string }[] = [
    { id: 'call', icon: <Icon.Phone size={14} sw={2.25} />, label: t('ct.cond.call') },
    { id: 'stage', icon: <Icon.Layers size={14} sw={2.25} />, label: t('ct.cond.stage') },
    { id: 'offer', icon: <Icon.Mail size={14} sw={2.25} />, label: t('ct.cond.offer') },
    { id: 'amount', icon: <Icon.Target size={14} sw={2.25} />, label: t('ct.cond.amount') },
  ]

  const collectConditions = (): string[] => [
    ...COND_DEFS.filter((c) => conds[c.id]).map((c) => c.label),
    ...customConds,
  ]

  const addCustom = () => {
    const v = customDraft.trim()
    if (!v) return
    setCustomConds((c) => [...c, v])
    setCustomDraft('')
  }

  // Picking a task type also seeds a default title when the field is still empty
  // (so the removed title-template chips aren't missed) — never clobbers typed text.
  const pickType = (id: TaskType) => {
    setTaskType(id)
    if (!title.trim()) {
      const tpl = typeTemplate(id)
      if (tpl) setTitle(tpl)
    }
  }

  const createMut = useMutation({
    mutationFn: () => createTask({
      manager_id: roundRobin ? null : managerId,
      deal_id: dealId,
      title: title.trim(),
      description: description.trim() || null,
      due_iso: dueIso(dueSel, dueCustom),
      priority,
      task_type: taskType,
      completion_conditions: collectConditions(),
      notify_telegram: notifyTg,
    }),
    onSuccess: () => {
      hapticNotify('success')
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['team'] })
      toast(t('ct.created', { m: dealLabel || '' }))
      navigate(-1)
    },
    onError: () => { hapticNotify('error'); toast(t('ct.createErr')) },
  })

  // ── AI parse: free-form → structured task → fill the manual form ───────────
  const parseMut = useMutation({
    mutationFn: () => parseTask(aiText.trim()),
    onSuccess: (r) => {
      if (r.deal_id != null) {
        setDealId(r.deal_id)
        setDealLabel(`#${r.deal_id}${r.client ? ` · ${r.client}` : ''}`)
      }
      if (r.task_type) setTaskType(r.task_type)
      if (r.manager_id != null) { setRoundRobin(false); setManagerId(r.manager_id) }
      if (r.priority) setPriority(r.priority)
      if (r.deadline_label) setDueSel(dueFromLabel(r.deadline_label))
      if (r.conditions.length) {
        setConds({ call: false, stage: false, offer: false, amount: false })
        setCustomConds(r.conditions)
      }
      if (r.comment) setDescription(r.comment)
      if (!title.trim()) setTitle(typeTitle(r.task_type, r.client))
      hapticNotify('success')
      setAiFilled(true)
      setMode('manual')
      toast(t('ct.ai.filled'))
    },
    onError: () => { hapticNotify('error'); toast(t('ct.ai.err')) },
  })

  // ── Voice dictation: speech → text (Web Speech API) ────────────────────────
  const stopVoice = () => {
    try { recRef.current?.stop() } catch { /* noop */ }
    setListening(false)
  }
  const startVoice = () => {
    const SR = (window as unknown as { SpeechRecognition?: any; webkitSpeechRecognition?: any })
    const Ctor = SR.SpeechRecognition || SR.webkitSpeechRecognition
    if (!Ctor) { toast(t('ct.ai.micErr')); return }
    try {
      const rec = new Ctor()
      const lang = (window as unknown as { __lang?: string }).__lang || 'ru'
      rec.lang = lang === 'uz' ? 'uz-UZ' : 'ru-RU'
      rec.continuous = true
      rec.interimResults = true
      baseRef.current = aiText.trim() ? aiText.trim() + ' ' : ''
      rec.onresult = (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => {
        let txt = ''
        for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript
        setAiText(baseRef.current + txt)
      }
      rec.onerror = (ev: { error?: string }) => {
        setListening(false); recRef.current = null
        if (ev && ev.error !== 'aborted') toast(t('ct.ai.micErr'))
      }
      rec.onend = () => { setListening(false); recRef.current = null }
      rec.start()
      recRef.current = rec
      setListening(true)
    } catch { setListening(false); toast(t('ct.ai.micErr')) }
  }
  const toggleVoice = () => (listening ? stopVoice() : startVoice())
  useEffect(() => () => { try { recRef.current?.stop() } catch { /* noop */ } }, [])

  const dueValid = dueSel !== 'custom' || !!dueCustom
  const managerValid = roundRobin || managerId != null
  // Required fields in visual/DOM order. A failed submit jumps to the FIRST
  // empty one, glows it, and names it — instead of the old silent dead button.
  const requiredFields = () => [
    { ok: !!dealId,       ref: dealRef,  toast: t('ct.reqDeal') },
    { ok: managerValid,   ref: respRef,  toast: t('ct.reqResp') },
    { ok: !!title.trim(), ref: titleRef, toast: t('ct.titleLabel') },
    { ok: dueValid,       ref: dueRef,   toast: t('ct.reqDeadline') },
  ]

  const submit = () => {
    setTouched(true)
    const missing = requiredFields().find((f) => !f.ok)
    if (missing) {
      missing.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      pulseGlow(missing.ref.current)
      hapticNotify('error')
      toast(missing.toast)
      return
    }
    createMut.mutate()
  }
  const parseNow = () => { if (aiText.trim() && !parseMut.isPending) parseMut.mutate() }

  // MainButton: parse in AI mode, create in manual mode.
  const mainAvailable = useMainButton(
    mode === 'ai'
      ? { text: parseMut.isPending ? t('ct.ai.parsing') : t('ct.ai.parse'), onClick: parseNow, enabled: !!aiText.trim() && !parseMut.isPending }
      : { text: createMut.isPending ? t('ct.creating') : t('ct.create'), onClick: submit, enabled: !createMut.isPending },
  )

  return (
    <>
      <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Header — title always; an in-app back button when outside Telegram
            (in Telegram the native top-bar BackButton handles it). */}
        <div className="row middle gap-2">
          {!isTelegramEnv() && (
            <button onClick={() => navigate(-1)} aria-label="Back"
              style={{ width: 36, height: 36, borderRadius: 9, border: 'none', background: 'var(--surface-2)', color: 'var(--text-1)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <Icon.ArrowLeft size={20} sw={2} />
            </button>
          )}
          <div className="t-h1">{t('ct.title')}</div>
        </div>

        <Segmented value={mode} onChange={setMode}
          options={[{ id: 'manual', label: t('ct.mode.manual') }, { id: 'ai', label: t('ct.mode.ai') }]} />

        {mode === 'ai' ? (
          <div className="col" style={{ gap: 14 }}>
            <div className="aicard" style={{ display: 'flex', gap: 10 }}>
              <Icon.Sparkle size={18} style={{ color: 'var(--ai)', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div className="t-label" style={{ color: 'var(--ai)' }}>{t('ct.ai.title')}</div>
                <div className="t-caption">{t('ct.ai.hint')}</div>
              </div>
            </div>

            {/* Voice dictation */}
            <div className="col" style={{ alignItems: 'center', gap: 10, padding: '6px 0 2px' }}>
              <button onClick={toggleVoice} aria-label={t('ct.ai.mic')}
                style={{
                  width: 76, height: 76, borderRadius: '50%', cursor: 'pointer', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                  background: listening ? 'var(--danger)' : 'var(--ai)',
                  boxShadow: listening
                    ? '0 0 0 10px color-mix(in srgb, var(--danger) 16%, transparent), 0 8px 22px color-mix(in srgb, var(--danger) 42%, transparent)'
                    : '0 8px 22px color-mix(in srgb, var(--ai) 38%, transparent)',
                  animation: listening ? 'pulse 1.3s ease-in-out infinite' : 'none',
                  transition: 'background 0.15s, box-shadow 0.2s',
                }}>
                <Icon.Mic size={30} sw={2} />
              </button>
              <span className="t-body-s" style={{ fontWeight: 600, color: listening ? 'var(--danger)' : 'var(--text-2)', textAlign: 'center' }}>
                {listening ? t('ct.ai.listening') : t('ct.ai.mic')}
              </span>
            </div>

            <textarea className="input" value={aiText} onChange={(e) => setAiText(e.target.value)}
              placeholder={t('ct.ai.ph')}
              style={{ width: '100%', height: 132, padding: '12px 14px', resize: 'none', lineHeight: 1.5 }} />

            <div className="row between middle">
              <button onClick={() => setAiText(t('ct.ai.exampleText'))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'transparent', border: '1px dashed var(--border)', borderRadius: 9, padding: '7px 11px', font: '500 var(--caption)/1 var(--font-sans)', color: 'var(--text-2)' }}>
                <Icon.Plus size={13} sw={2.5} />{t('ct.ai.example')}
              </button>
              {aiText.trim() && (
                <button onClick={() => setAiText('')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', background: 'transparent', border: 'none', padding: '7px 6px', font: '500 var(--caption)/1 var(--font-sans)', color: 'var(--text-3)' }}>
                  <Icon.X size={13} sw={2.5} />{t('btn.cancel')}
                </button>
              )}
            </div>

            <div className="row gap-2" style={{ alignItems: 'flex-start', color: 'var(--text-3)' }}>
              <Icon.Mic size={14} sw={2} style={{ marginTop: 1, flexShrink: 0 }} />
              <span className="t-caption" style={{ color: 'var(--text-3)' }}>{t('ct.ai.micHint')}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="t-caption" style={{ marginTop: -8 }}>{t('ct.subtitle')}</div>

            {aiFilled && (
              <div className="aicard" style={{ display: 'flex', gap: 10 }}>
                <Icon.Sparkle size={18} style={{ color: 'var(--ai)', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div className="t-label" style={{ color: 'var(--ai)' }}>{t('ct.ai.reading')}</div>
                  <div className="t-caption">{t('ct.ai.banner')}</div>
                </div>
              </div>
            )}

            {/* Deal */}
            <div ref={dealRef}>
              <Field label={t('ct.deal')} icon={<Icon.Briefcase size={17} sw={2} />}
                value={dealLabel} placeholder={t('ct.dealPick')}
                error={touched && !dealId} onClick={() => setDealOpen(true)} />
            </div>

            {/* Task type */}
            <div>
              <div className="t-label" style={{ marginBottom: 8 }}>{t('ct.type')}</div>
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                {TYPES.map((tt) => (
                  <Chip key={tt.id} active={taskType === tt.id} icon={tt.icon} onClick={() => pickType(tt.id)}>{tt.label}</Chip>
                ))}
              </div>
            </div>

            {/* Responsible */}
            <div ref={respRef}>
              <div className="row between middle" style={{ marginBottom: 8 }}>
                <div className="t-label">{t('ct.resp')}</div>
                <div className="t-caption">{t('ct.respHint')}</div>
              </div>

              {/* Round-robin */}
              <div className="radio-row" onClick={() => { setRoundRobin(true); setManagerId(null) }}
                style={{ border: `1px solid ${roundRobin ? 'var(--brand-500)' : 'var(--border)'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                <span className="radio-dot" />
                <span style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ai-bg)', color: 'var(--ai)' }}>
                  <Icon.Sparkle size={18} />
                </span>
                <div className="flex-1" style={{ minWidth: 0 }}>
                  <div className="t-body-s">{t('ct.rr')}</div>
                  <div className="t-caption">{t('ct.rrHint')}</div>
                </div>
              </div>

              <div className="col" style={{ gap: 8, opacity: roundRobin ? 0.55 : 1 }}>
                {team.isLoading && <div className="t-caption" style={{ padding: '8px 4px' }}>…</div>}
                {team.isError && <div className="t-caption" style={{ padding: '8px 4px', color: 'var(--danger)' }}>{t('ct.loadTeamErr')}</div>}
                {managers.map((m) => (
                  <ManagerRow key={m.id} m={m}
                    selected={!roundRobin && managerId === m.id}
                    recommended={m.id === recommendedId}
                    error={touched && !managerValid}
                    onPick={() => { setRoundRobin(false); setManagerId(m.id) }} />
                ))}
              </div>
            </div>

            {/* Title — type chips above prefill a sensible default; free-text here. */}
            <div ref={titleRef}>
              <div className="t-label" style={{ marginBottom: 6 }}>{t('ct.titleLabel')}</div>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('ct.titlePh')}
                style={{ width: '100%', borderColor: touched && !title.trim() ? 'var(--danger)' : undefined }} />
            </div>

            {/* Deadline (preset chips) + priority */}
            <div ref={dueRef}>
              <div className="t-label" style={{ marginBottom: 8 }}>{t('ct.dueLabel')}</div>
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                <DueChip active={dueSel === 'in1h'} label={t('ct.dueIn1h')} onClick={() => setDueSel('in1h')} />
                <DueChip active={dueSel === 'today18'} label={t('ct.dueToday')} onClick={() => setDueSel('today18')} />
                <DueChip active={dueSel === 'tom10'} label={t('ct.dueTom10')} onClick={() => setDueSel('tom10')} />
                <DueChip active={dueSel === 'custom'} label={t('ct.dueCustom')} onClick={() => setDueSel('custom')} />
              </div>
              {dueSel === 'custom' && (
                <input type="datetime-local" value={dueCustom} onChange={(e) => setDueCustom(e.target.value)}
                  style={{ width: '100%', marginTop: 8, padding: 10, borderRadius: 8, border: `1px solid ${touched && !dueCustom ? 'var(--danger)' : 'var(--border)'}`, background: 'var(--surface)', color: 'var(--text-1)', font: 'inherit', outline: 'none' }} />
              )}
            </div>

            <div>
              <div className="t-label" style={{ marginBottom: 8 }}>{t('ct.prio')}</div>
              <div className="row gap-2">
                <PrioChip active={priority === 'normal'} label={t('ct.prio.normal')} color="var(--text-2)" onClick={() => setPriority('normal')} />
                <PrioChip active={priority === 'high'} label={t('ct.prio.high')} color="var(--warning)" icon onClick={() => setPriority('high')} />
                <PrioChip active={priority === 'urgent'} label={t('ct.prio.critical')} color="var(--danger)" icon onClick={() => setPriority('urgent')} />
              </div>
            </div>

            {/* Completion conditions */}
            <div>
              <div className="t-label" style={{ marginBottom: 4 }}>{t('ct.cond')}</div>
              <div className="t-caption" style={{ marginBottom: 8 }}>{t('ct.condHint')}</div>
              <div className="card" style={{ padding: 4 }}>
                {COND_DEFS.map((c, i) => (
                  <div key={c.id} onClick={() => setConds((s) => ({ ...s, [c.id]: !s[c.id] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px', cursor: 'pointer', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                    <Checkbox checked={!!conds[c.id]} />
                    <span style={{ color: conds[c.id] ? 'var(--brand-900)' : 'var(--text-3)', display: 'flex' }}>{c.icon}</span>
                    <span className="t-body-s flex-1" style={{ fontWeight: 500, color: conds[c.id] ? 'var(--text-1)' : 'var(--text-2)' }}>{c.label}</span>
                  </div>
                ))}
                {customConds.map((cv, i) => (
                  <div key={`cu${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px', borderTop: '1px solid var(--border)' }}>
                    <Checkbox checked />
                    <span style={{ color: 'var(--brand-900)', display: 'flex' }}><Icon.Check size={14} sw={2.5} /></span>
                    <span className="t-body-s flex-1" style={{ fontWeight: 500 }}>{cv}</span>
                    <button className="ibtn ibtn-sm" onClick={(e) => { e.stopPropagation(); setCustomConds((c) => c.filter((_, j) => j !== i)) }} style={{ background: 'transparent' }}>
                      <Icon.X size={15} sw={2.25} style={{ color: 'var(--text-3)' }} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="row gap-2" style={{ marginTop: 8 }}>
                <input className="input" value={customDraft} onChange={(e) => setCustomDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCustom() }}
                  placeholder={t('ct.condCustomPh')} style={{ flex: 1 }} />
                <Button variant="ghost" icon={<Icon.Plus size={16} sw={2.5} />} onClick={addCustom}
                  style={{ opacity: customDraft.trim() ? 1 : 0.4, pointerEvents: customDraft.trim() ? 'auto' : 'none', flexShrink: 0 }}>
                  {t('ct.condAdd')}
                </Button>
              </div>
            </div>

            {/* Comment */}
            <div>
              <div className="t-label" style={{ marginBottom: 8 }}>{t('ct.comment')}</div>
              <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder={t('ct.commentPh')} style={{ width: '100%', height: 76, padding: '10px 12px', resize: 'none', lineHeight: 1.4 }} />
            </div>

            {/* Notify Telegram */}
            <div className="card card-16" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
              <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', color: 'var(--ai)' }}>
                <Icon.Telegram size={18} sw={2} />
              </span>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="t-body-s">{t('ct.notifyTg')}</div>
                <div className="t-caption">{t('ct.notifyTgHint')}</div>
              </div>
              <Toggle value={notifyTg} onChange={setNotifyTg} />
            </div>
          </>
        )}
      </div>

      {/* Sticky footer — fallback when MainButton unavailable */}
      {!mainAvailable && (
        <div style={{ flexShrink: 0, padding: '10px 16px 14px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
          {mode === 'ai' ? (
            <Button variant="primary" full
              icon={<Icon.Sparkle size={17} sw={2.25} style={parseMut.isPending ? { animation: 'pulse 1.1s ease-in-out infinite' } : undefined} />}
              onClick={parseNow}
              style={{ opacity: aiText.trim() && !parseMut.isPending ? 1 : 0.5, pointerEvents: aiText.trim() && !parseMut.isPending ? 'auto' : 'none' }}>
              {parseMut.isPending ? t('ct.ai.parsing') : t('ct.ai.parse')}
            </Button>
          ) : (
            <Button variant="primary" full icon={<Icon.Check size={17} sw={2.5} />} onClick={submit}
              style={{ opacity: createMut.isPending ? 0.5 : 1, pointerEvents: createMut.isPending ? 'none' : 'auto' }}>
              {createMut.isPending ? t('ct.creating') : t('ct.create')}
            </Button>
          )}
        </div>
      )}

      {dealOpen && (
        <DealPickerSheet onClose={() => setDealOpen(false)}
          onPick={(d) => { setDealId(d.id); setDealLabel(`#${d.id}${d.client_name ? ` · ${d.client_name}` : ''}`); setDealOpen(false) }} />
      )}
    </>
  )
}

// One-off red glow pulse on a section the director left empty. Web Animations
// API (not a CSS keyframe) so it restarts cleanly on every submit attempt and
// the change stays inside this file. Fades to nothing; the persistent red
// outline (error borders) is what keeps the field marked until it's filled.
function pulseGlow(el: HTMLElement | null) {
  if (!el || typeof el.animate !== 'function') return
  el.animate(
    [
      { boxShadow: '0 0 0 0 rgba(220,38,38,0)' },
      { boxShadow: '0 0 0 4px rgba(220,38,38,0.35)' },
      { boxShadow: '0 0 0 0 rgba(220,38,38,0)' },
    ],
    { duration: 900, iterations: 2, easing: 'ease-out' },
  )
}

// ── Segmented control (Вручную / AI) ────────────────────────────────────────
function Segmented<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: { id: T; label: string }[]
}) {
  return (
    <div className="row" style={{ gap: 6, background: 'var(--surface-2)', padding: 4, borderRadius: 12 }}>
      {options.map((o) => {
        const active = o.id === value
        return (
          <button key={o.id} type="button" onClick={() => onChange(o.id)}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', cursor: 'pointer', font: '600 14px/1 var(--font-sans)', background: active ? 'var(--surface)' : 'transparent', color: active ? 'var(--text-1)' : 'var(--text-3)', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'background 0.15s, color 0.15s' }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${checked ? 'var(--brand-500)' : 'var(--border-strong)'}`, background: checked ? 'var(--brand-500)' : 'transparent', color: '#fff' }}>
      {checked && <Icon.Check size={12} sw={3} />}
    </span>
  )
}

function LoadBar({ pct, tasks, norm }: { pct: number; tasks: number; norm: number }) {
  const color = pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warning)' : 'var(--success)'
  return (
    <div className="row middle gap-2" style={{ marginTop: 5 }}>
      <span style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--surface-2)', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
      </span>
      <span className="t-caption" style={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{pct}% ({tasks}/{norm})</span>
    </div>
  )
}

function Field({ label, icon, value, placeholder, error, onClick }: {
  label: string; icon: ReactNode; value: string; placeholder: string; error?: boolean; onClick: () => void
}) {
  return (
    <div>
      <div className="t-label" style={{ marginBottom: 6 }}>{label}</div>
      <button className="card card-16" onClick={onClick}
        style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`, background: 'var(--surface)' }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: value ? 'var(--brand-100)' : 'var(--surface-2)', color: value ? 'var(--brand-900)' : 'var(--text-3)' }}>{icon}</span>
        <span className="flex-1" style={{ minWidth: 0 }}>
          {value
            ? <span className="t-body-s ellipsis" style={{ display: 'block' }}>{value}</span>
            : <span className="t-body-s" style={{ display: 'block', color: 'var(--text-3)', fontWeight: 400 }}>{placeholder}</span>}
        </span>
        <Icon.Chevron size={18} sw={2} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
      </button>
    </div>
  )
}

function ManagerRow({ m, selected, recommended, error, onPick }: {
  m: TeamMemberCard; selected: boolean; recommended?: boolean; error?: boolean; onPick: () => void
}) {
  const online = m.status === 'active'
  const load = m.stats.load_pct
  const overload = load >= 90
  return (
    <div className={`radio-row ${selected ? 'is-selected' : ''}`} onClick={onPick}
      style={{ border: `1px solid ${selected ? 'var(--brand-500)' : (error ? 'var(--danger)' : 'var(--border)')}`, borderRadius: 10, padding: '10px 12px', opacity: m.inactive_this_window ? 0.6 : 1 }}>
      <span className="radio-dot" />
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar name={m.name || m.short_name} size={36} />
        <span style={{ position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: '50%', border: '2px solid var(--surface)', background: online ? 'var(--success)' : 'var(--text-3)' }} />
      </div>
      <div className="flex-1" style={{ minWidth: 0 }}>
        <div className="row middle gap-2" style={{ flexWrap: 'wrap' }}>
          <span className="t-body-s ellipsis">{m.name || m.short_name}</span>
          {recommended && <Badge tone="ai" sm><Icon.Sparkle size={9} sw={2.5} />{t('assign.recommended')}</Badge>}
          {overload && <Badge tone="danger" sm>{t('assign.overload')}</Badge>}
        </div>
        <div className="t-caption">
          {t('md.kpiOpen')}: {m.stats.open_tasks} · {t('md.kpiOverdue')}: {m.stats.overdue}
          {m.stats.avg_score != null && <> · {t('md.kpiScore')} {m.stats.avg_score.toFixed(1)}</>}
        </div>
        <LoadBar pct={load} tasks={m.stats.open_tasks} norm={m.stats.norm} />
      </div>
    </div>
  )
}

function Chip({ active, icon, onClick, children }: { active: boolean; icon?: ReactNode; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', font: '600 var(--body-s)/1 var(--font-sans)', border: `1.5px solid ${active ? 'var(--brand-500)' : 'var(--border)'}`, background: active ? 'var(--brand-100)' : 'var(--surface)', color: active ? 'var(--brand-900)' : 'var(--text-2)' }}>
      {icon}{children}
    </button>
  )
}

function DueChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', font: '600 var(--body-s)/1 var(--font-sans)', border: `1.5px solid ${active ? 'var(--brand-500)' : 'var(--border)'}`, background: active ? 'var(--brand-100)' : 'var(--surface)', color: active ? 'var(--brand-900)' : 'var(--text-2)' }}>
      <Icon.Clock size={13} sw={2.25} />{label}
    </button>
  )
}

function PrioChip({ active, label, color, icon, onClick }: { active: boolean; label: string; color: string; icon?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 10px', borderRadius: 10, cursor: 'pointer', font: '600 var(--body-s)/1 var(--font-sans)', border: `1.5px solid ${active ? color : 'var(--border)'}`, background: active ? `color-mix(in srgb, ${color} 12%, transparent)` : 'var(--surface)', color: active ? color : 'var(--text-2)' }}>
      {icon && <Icon.AlertTri size={13} sw={2.25} />}{label}
    </button>
  )
}

// Toggle (iOS-style) — local to avoid threading the design facade's any-typed one.
function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!value)}
      style={{ width: 44, height: 26, borderRadius: 13, background: value ? 'var(--success)' : 'var(--border-strong)', position: 'relative', border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 2, left: value ? 20 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
    </button>
  )
}

// ── deal picker sheet ───────────────────────────────────────────────────────
// A row needs only id/name/amount; both the browse list (DealListItem) and the
// server-search hit (SearchDeal) collapse to this so the renderer is uniform.
type PickRow = { id: number; client_name: string | null; amount: number | null }

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

function DealPickerSheet({ onClose, onPick }: { onClose: () => void; onPick: (d: PickRow) => void }) {
  const [q, setQ] = useState('')
  const needle = q.trim()
  const debounced = useDebounced(needle, 300)
  const searching = debounced.length >= 2

  // Empty box → browse the most-recently-modified open deals (a quick default
  // pick list). The moment the director types, hit the SERVER search (substring
  // /id over the whole tenant) instead of filtering this one 50-deal page: a
  // stale backlog deal lives past the first page and is exactly the deal someone
  // opens this picker to find — the client-side filter could never surface it.
  const browse = useQuery({
    queryKey: ['deals', 'pick'],
    queryFn: () => fetchDeals({ per_page: 50 }),
    enabled: !searching,
  })
  const found = useQuery({
    queryKey: ['deal-search', debounced],
    queryFn: () => fetchSearch(debounced, 20),
    enabled: searching,
  })

  const loading = searching ? found.isFetching : browse.isLoading
  const isError = searching && found.isError
  const list: PickRow[] = useMemo(() => {
    if (searching) {
      return (found.data?.deals ?? []).map((d) => ({ id: d.id, client_name: d.client_name, amount: d.amount_uzs }))
    }
    return (browse.data?.deals ?? []).map((d) => ({ id: d.id, client_name: d.client_name, amount: d.amount }))
  }, [searching, found.data, browse.data])

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="t-h1">{t('ct.dealSheet')}</div>
        <div style={{ position: 'relative', margin: '10px 0 6px' }}>
          <Icon.Search size={16} sw={2} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ct.dealSearch')} style={{ paddingLeft: 36, width: '100%' }} />
        </div>
        <div className="col" style={{ gap: 6, maxHeight: 360, overflowY: 'auto', marginTop: 4 }}>
          {loading && <div className="t-caption" style={{ padding: '12px 4px', textAlign: 'center', color: 'var(--text-3)' }}>…</div>}
          {list.map((d) => (
            <div key={d.id} className="card card-16" onClick={() => onPick(d)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px' }}>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="t-body-s ellipsis">{d.client_name || `#${d.id}`}</div>
                <div className="t-caption">
                  <span className="t-mono" style={{ color: 'var(--text-3)' }}>#{d.id}</span>
                  {d.amount != null && <> · <Money uzs={d.amount} /></>}
                </div>
              </div>
              <Icon.Chevron size={16} sw={2} style={{ color: 'var(--text-3)' }} />
            </div>
          ))}
          {!loading && isError && (
            <div className="t-caption" style={{ padding: '12px 4px', textAlign: 'center', color: 'var(--danger)' }}>{t('srch.err')}</div>
          )}
          {!loading && !isError && list.length === 0 && (
            <div className="t-caption" style={{ padding: '12px 4px', textAlign: 'center', color: 'var(--text-3)' }}>
              {searching ? t('srch.empty', { q: debounced }) : '—'}
            </div>
          )}
        </div>
        <Button variant="ghost" full onClick={onClose} style={{ marginTop: 10 }}>{t('btn.cancel')}</Button>
      </div>
    </div>
  )
}

// Map a deadline-label keyword (from AI parse) to a preset due chip.
function dueFromLabel(label: string): DueId {
  const s = label.toLowerCase()
  if (/час|soat|соат|hour/.test(s)) return 'in1h'
  if (/завтра|ertaga|эртага|tomorrow/.test(s)) return 'tom10'
  return 'today18'
}

// Default title for a task type (empty for meeting — no sensible template).
function typeTemplate(type: TaskType | null): string {
  return type === 'message' ? t('ct.tplMsg') : type === 'doc' ? t('ct.tplDoc') : type === 'call' ? t('ct.tplCall') : ''
}

// Title fallback when the AI didn't dictate one — derive from type + client.
function typeTitle(type: TaskType | null, client: string): string {
  const base = typeTemplate(type) || t('ct.tplCall')
  return client ? `${base} · ${client}` : base
}

// Map a due option to an absolute ISO instant.
function dueIso(id: DueId, custom: string): string {
  const now = new Date()
  const at = (h: number, m: number, addDays = 0) => {
    const d = new Date(now)
    d.setDate(d.getDate() + addDays)
    d.setHours(h, m, 0, 0)
    return d
  }
  switch (id) {
    case 'in1h': return new Date(now.getTime() + 60 * 60 * 1000).toISOString()
    case 'today18': {
      const d = at(18, 0)
      return (d.getTime() <= now.getTime() ? at(18, 0, 1) : d).toISOString()
    }
    case 'tom10': return at(10, 0, 1).toISOString()
    case 'custom': return custom ? new Date(custom).toISOString() : new Date(now.getTime() + 60 * 60 * 1000).toISOString()
    default: return at(18, 0).toISOString()
  }
}
